import { useEffect, useState } from 'react';
import { Receipt, ExternalLink, RefreshCw, CheckCircle2, XCircle, Send } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { useToast } from '../../components/Toast';
import { supabase } from '../../lib/supabase';
import styles from './finance.module.css';

const ICOUNT_URL = 'https://app.icount.co.il';

interface InvoiceRow {
  id: string;
  amount: number;
  currency: string;
  status: string;
  invoice_number: string;
  project_id: string;
  sent_at: string | null;
  paid_at: string | null;
  notes: string | null;
  created_at: string;
}

interface ICountInvoice {
  doc_id: number | string;
  client_name?: string;
  total?: number;
  doc_total?: number;
  doc_date?: string;
  due_date?: string;
  paid?: boolean;
  status?: string;
  pdf_link?: string;
  doc_number?: string | number;
}

interface ConnTestResult {
  ok: boolean;
  company?: string;
  message?: string;
}

function formatILS(amount: number): string {
  return `₪ ${new Intl.NumberFormat('he-IL', { maximumFractionDigits: 0 }).format(amount)}`;
}

interface EdgeResult { ok: boolean; data: unknown; reason: string | null; raw: Record<string, unknown> }

async function callEdge(operation: string, extra: Record<string, unknown> = {}): Promise<EdgeResult> {
  const { data: session } = await supabase.auth.getSession();
  const token = session.session?.access_token;
  if (!token) return { ok: false, data: null, reason: 'No auth session', raw: {} };
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/icount-sync`;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ operation, ...extra }),
    });
    const json = await res.json() as Record<string, unknown> & { ok?: boolean; data?: unknown; reason?: string };
    return {
      ok: json.ok ?? false,
      data: json.data ?? null,
      reason: json.reason ?? null,
      raw: json,
    };
  } catch (err) {
    return { ok: false, data: null, reason: String(err), raw: { error: String(err) } };
  }
}

export function FinancePage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const toast = useToast();

  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [icountRows, setIcountRows] = useState<ICountInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [conn, setConn] = useState<ConnTestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [creating, setCreating] = useState<string | null>(null);
  const [projectNames, setProjectNames] = useState<Record<string, string>>({});
  const [debugDump, setDebugDump] = useState<string | null>(null);
  // Off unless ticked, and cleared after every invoice: an email to a client
  // cannot be taken back, so it is never the default and never sticky.
  const [sendToClient, setSendToClient] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    const cancel = { current: false };
    void (async () => {
      const [invRes, projRes] = await Promise.all([
        supabase
          .from('project_invoices')
          .select('id, amount, currency, status, invoice_number, project_id, sent_at, paid_at, notes, created_at')
          .eq('organization_id', orgId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .limit(100),
        supabase
          .from('projects')
          .select('id, name')
          .eq('organization_id', orgId)
          .is('deleted_at', null),
      ]);
      if (cancel.current) return;
      setRows(invRes.data ?? []);
      const names: Record<string, string> = {};
      (projRes.data ?? []).forEach((p) => { names[p.id] = p.name; });
      setProjectNames(names);
      setLoading(false);
    })();
    return () => { cancel.current = true; };
  }, [orgId]);

  async function handleTest() {
    setTesting(true);
    const r = await callEdge('test');
    setTesting(false);
    if (r.ok) {
      const d = r.data as { client_info?: { company_name?: string; cid?: string } } | null;
      const company = d?.client_info?.company_name ?? 'iCount';
      setConn({ ok: true, company, message: 'מחובר' });
      toast.success(`מחובר ל-${company}`);
    } else {
      setConn({ ok: false, message: r.reason ?? 'תקלה' });
      toast.error(r.reason ?? 'תקלה בחיבור');
    }
  }

  async function handleSyncFromICount() {
    setSyncing(true);
    const r = await callEdge('list_invoices', { limit: 50 });
    setSyncing(false);
    if (!r.ok) {
      const debug = JSON.stringify(r.raw, null, 2);
      console.error('iCount list failed:', debug);
      setDebugDump(debug);
      toast.error(r.reason ?? 'תקלה בסנכרון מול iCount');
      return;
    }
    setDebugDump(null);
    const data = r.data as { doc_list?: ICountInvoice[] } | ICountInvoice[] | null;
    let list: ICountInvoice[] = [];
    if (Array.isArray(data)) list = data;
    else if (data && Array.isArray(data.doc_list)) list = data.doc_list;
    setIcountRows(list);
    const which = (r as unknown as { which_endpoint?: string }).which_endpoint;
    toast.success(`נטענו ${String(list.length)} חשבוניות${which ? ` · ${which}` : ''}`);
  }

  async function handleCreateInICount(invoice: InvoiceRow) {
    const delivery = sendToClient ? 'ותישלח ללקוח במייל' : 'ולא תישלח ללקוח';
    if (!window.confirm(`ליצור חשבונית ב-iCount לסכום ${formatILS(invoice.amount)}? החשבונית תיווצר ${delivery}.`)) return;
    setCreating(invoice.id);
    const r = await callEdge('create_invoice', { invoice_id: invoice.id, send_email: sendToClient });
    setCreating(null);
    setSendToClient(false);
    if (r.ok) {
      const d = r.data as { icount_doc_id?: string; pdf_link?: string } | null;
      toast.success(`נוצרה חשבונית ב-iCount${d?.icount_doc_id ? ` · ${d.icount_doc_id}` : ''}`);
      // Refresh
      const { data } = await supabase
        .from('project_invoices')
        .select('id, amount, currency, status, invoice_number, project_id, sent_at, paid_at, notes, created_at')
        .eq('organization_id', orgId ?? '')
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(100);
      setRows(data ?? []);
    } else {
      toast.error(r.reason ?? 'תקלה ביצירה');
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1>כספים</h1>
          <p className={styles.sub}>חשבוניות + סנכרון עם iCount. הטוקן ב-Supabase secrets קורא ל-API ישירות מהשרת — לא נחשף ל-frontend.</p>
        </div>
        <Receipt size={32} className={styles.headIcon} aria-hidden="true" />
      </header>

      {/* Connection status + controls */}
      <div className={styles.banner}>
        <div className={styles.bannerLeft}>
          {conn === null ? (
            <strong>חיבור iCount: לא נבדק</strong>
          ) : conn.ok ? (
            <>
              <CheckCircle2 size={20} className={styles.iconOk} aria-hidden="true" />
              <div>
                <strong>מחובר ל-{conn.company}</strong>
                <p>טוקן תקין · קריאות ל-iCount API פעילות</p>
              </div>
            </>
          ) : (
            <>
              <XCircle size={20} className={styles.iconErr} aria-hidden="true" />
              <div>
                <strong>חיבור נכשל</strong>
                <p>{conn.message}</p>
              </div>
            </>
          )}
        </div>
        <div className={styles.bannerActions}>
          <button type="button" className={styles.btnSecondary} onClick={() => { void handleTest(); }} disabled={testing}>
            <RefreshCw size={14} aria-hidden="true" />
            {testing ? 'בודק...' : 'בדוק חיבור'}
          </button>
          <button type="button" className={styles.btnSecondary} onClick={() => { void handleSyncFromICount(); }} disabled={syncing}>
            <RefreshCw size={14} aria-hidden="true" />
            {syncing ? 'מסנכרן...' : 'משוך מ-iCount'}
          </button>
          <a href={ICOUNT_URL} target="_blank" rel="noreferrer noopener" className={styles.btnSecondary}>
            <ExternalLink size={14} aria-hidden="true" />
            פתח iCount
          </a>
        </div>
      </div>

      {/* Debug dump — shows raw iCount API responses when listing fails */}
      {debugDump !== null ? (
        <div className={styles.section}>
          <h3>iCount API · raw_attempts (debug)</h3>
          <pre dir="ltr" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, background: '#f9fafb', padding: 12, borderRadius: 8, maxHeight: 400, overflow: 'auto' }}>
            {debugDump}
          </pre>
        </div>
      ) : null}

      {/* iCount invoices (live from API) */}
      {icountRows.length > 0 ? (
        <div className={styles.section}>
          <h3>חשבוניות מ-iCount ({String(icountRows.length)})</h3>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>מספר</th>
                <th>לקוח</th>
                <th>תאריך</th>
                <th>סכום</th>
                <th>סטטוס</th>
                <th>PDF</th>
              </tr>
            </thead>
            <tbody>
              {icountRows.map((d, idx) => (
                <tr key={`${String(d.doc_id)}-${String(idx)}`}>
                  <td><code dir="ltr">{String(d.doc_number ?? d.doc_id)}</code></td>
                  <td>{d.client_name ?? '—'}</td>
                  <td className={styles.muted}>
                    {d.doc_date ? format(new Date(d.doc_date), 'dd/MM/yy', { locale: he }) : '—'}
                  </td>
                  <td><bdi>{formatILS(d.total ?? d.doc_total ?? 0)}</bdi></td>
                  <td>
                    <span className={styles.statusBadge}>
                      {d.paid ? 'שולמה' : (d.status ?? 'פתוחה')}
                    </span>
                  </td>
                  <td>
                    {d.pdf_link ? (
                      <a href={d.pdf_link} target="_blank" rel="noreferrer noopener" className={styles.link}>פתח</a>
                    ) : <span className={styles.muted}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Local project_invoices */}
      <div className={styles.section}>
        <h3>חשבוניות במערכת ({String(rows.length)})</h3>
        <label className={styles.sendToggle}>
          <input
            type="checkbox"
            checked={sendToClient}
            onChange={(e) => { setSendToClient(e.target.checked); }}
          />
          שלח ללקוח במייל את החשבונית הבאה שתיווצר ב-iCount
        </label>
        {loading ? (
          <p className={styles.notice}>טוען...</p>
        ) : rows.length === 0 ? (
          <p className={styles.notice}>אין חשבוניות במערכת. רושמים חשבונית ותשלומים מתוך הפרויקט, בטאב כספים.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>מספר</th>
                <th>פרויקט</th>
                <th>סטטוס</th>
                <th>סכום</th>
                <th>נשלחה</th>
                <th>שולמה</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const alreadyInICount = r.notes?.includes('iCount doc_id') ?? false;
                return (
                  <tr key={r.id}>
                    <td><code dir="ltr">{r.invoice_number}</code></td>
                    <td>{projectNames[r.project_id] ?? <span className={styles.muted}>—</span>}</td>
                    <td><span className={styles.statusBadge}>{r.status}</span></td>
                    <td><bdi>{formatILS(r.amount)}</bdi></td>
                    <td className={styles.muted}>{r.sent_at ? format(new Date(r.sent_at), 'dd/MM/yy', { locale: he }) : '—'}</td>
                    <td className={styles.muted}>{r.paid_at ? format(new Date(r.paid_at), 'dd/MM/yy', { locale: he }) : '—'}</td>
                    <td>
                      {alreadyInICount ? (
                        <span className={styles.muted}>ב-iCount</span>
                      ) : (
                        <button
                          type="button"
                          className={styles.btnPrimary}
                          onClick={() => { void handleCreateInICount(r); }}
                          disabled={creating === r.id || conn?.ok !== true}
                          title={conn?.ok !== true ? 'בדוק חיבור קודם' : undefined}
                        >
                          <Send size={14} aria-hidden="true" />
                          {creating === r.id ? 'יוצר...' : 'צור ב-iCount'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
